import { fetchOncallDuty } from "@/api/duty";
import {
  MemberResponse,
  TeamMemberResponse,
  addTeamMember,
  deleteTeamMember,
  fetchMembers,
  fetchTeamMembers,
} from "@/api/member";
import { useMemberNameMap } from "@/shared/hooks/useMemberNameMap";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { toast } from "@/shared/hooks/useToast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

// ── helpers ───────────────────────────────────────────────────────────────────

function toLocalDateStr(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function today() {
  return toLocalDateStr(new Date());
}

function tomorrow() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return toLocalDateStr(d);
}


// ── AddTeamMemberModal ────────────────────────────────────────────────────────

interface AddTeamMemberModalProps {
  open: boolean;
  onClose: () => void;
  allMembers: MemberResponse[];
  teamMemberIds: Set<number>;
  onAdd: (memberId: number) => void;
  onPhoneMissing: (member: MemberResponse) => void;
  isPending: boolean;
}

function hasPhone(member: MemberResponse): boolean {
  return !!member.phone && member.phone.trim().length > 0;
}

function AddTeamMemberModal({
  open,
  onClose,
  allMembers,
  teamMemberIds,
  onAdd,
  onPhoneMissing,
  isPending,
}: AddTeamMemberModalProps) {
  const candidates = allMembers.filter((m) => !teamMemberIds.has(m.id));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>팀원 추가</DialogTitle>
        </DialogHeader>
        <Command>
          <CommandInput placeholder="이름 또는 이메일 검색..." />
          <CommandList>
            {candidates.length === 0 ? (
              <CommandEmpty>추가할 수 있는 멤버가 없습니다.</CommandEmpty>
            ) : (
              <>
                <CommandEmpty>일치하는 멤버가 없습니다.</CommandEmpty>
                <CommandGroup>
                  {candidates.map((m) => {
                    const phoneRegistered = hasPhone(m);
                    return (
                      <CommandItem
                        key={m.id}
                        value={`${m.name} ${m.email}`}
                        disabled={isPending}
                        onSelect={() => {
                          if (!phoneRegistered) {
                            onPhoneMissing(m);
                            return;
                          }
                          onAdd(m.id);
                        }}
                      >
                        <div className="flex flex-1 items-center justify-between gap-2">
                          <div className={`flex flex-col ${phoneRegistered ? "" : "opacity-50"}`}>
                            <span className="text-sm">{m.name}</span>
                            <span className="text-xs text-muted-foreground">{m.email}</span>
                          </div>
                          {!phoneRegistered && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded border border-muted-foreground/30 text-muted-foreground whitespace-nowrap">
                              전화번호 미등록
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

// ── TeamMembersPanel ──────────────────────────────────────────────────────────

/** 팀 관리 > 팀 설정 다이얼로그의 팀원 목록·추가·삭제 (SPT-4066: 팀원 관리 페이지 통합) */
export function TeamMembersPanel({ teamId }: { teamId: number }) {
  const qc = useQueryClient();

  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [phoneMissingMember, setPhoneMissingMember] = useState<MemberResponse | null>(null);

  useEffect(() => {
    setSelected(new Set());
  }, [teamId]);

  // ── queries ───────────────────────────────────────────────────────────────
  const { data: teamMembers = [] } = useQuery<TeamMemberResponse[]>({
    queryKey: ["team-members", teamId],
    queryFn: () => fetchTeamMembers(teamId),
  });

  const { data: allMembers = [] } = useQuery<MemberResponse[]>({
    queryKey: ["members"],
    queryFn: fetchMembers,
  });

  const { getName } = useMemberNameMap();

  // 오늘 duty 조회 (primary 삭제 방지용). 스케줄 변경 시 ["duty-today", teamId] invalidate 에 함께 갱신된다
  // 날짜를 키에 넣어 자정이 지나면 새로 조회한다
  const todayStr = today();
  const { data: dutyData } = useQuery({
    queryKey: ["duty-today", teamId, "team-members", todayStr],
    // 백엔드는 starts_at < ends_at 만 허용하므로 내일까지 조회
    queryFn: () => fetchOncallDuty(teamId, todayStr, tomorrow()),
  });

  // ── derived ───────────────────────────────────────────────────────────────
  const memberMap = new Map(allMembers.map((m) => [m.id, m]));
  const teamMemberIds = new Set(teamMembers.map((tm) => tm.member_id));

  // primary 판단: 오늘 duty entry 의 첫 member (override 우선, 없으면 rotation 계산값).
  // 백엔드가 더 이상 team_member.role 컬럼을 관리하지 않으므로 duty 결과로만 판단한다.
  // entry.date 는 "YYYY-MM-DDT00:00:00Z" 형식이라 날짜 부분만 비교
  const todayPrimaryId =
    dutyData?.entries.find((e) => e.date.slice(0, 10) === todayStr)?.member_ids[0] ?? null;
  const isPrimary = (memberId: number) => todayPrimaryId === memberId;

  // ── mutations ─────────────────────────────────────────────────────────────
  const addMut = useMutation({
    mutationFn: (memberId: number) => addTeamMember(teamId, memberId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team-members", teamId] });
      setAddOpen(false);
    },
    onError: (error, memberId) => {
      const message = error instanceof Error ? error.message : "";
      // 백엔드 PHONE_REQUIRED 응답: API 직접 호출 등으로 프론트 사전 차단을 우회한 경우의 방어
      if (message.toLowerCase().includes("phone")) {
        setPhoneMissingMember(memberMap.get(memberId) ?? null);
        return;
      }
      toast({
        variant: "destructive",
        title: "멤버 추가 실패",
        description: message || "팀 멤버 추가 중 오류가 발생했습니다.",
      });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selected);
      const results = await Promise.allSettled(ids.map((id) => deleteTeamMember(teamId, id)));
      const succeededIds = ids.filter((_, i) => results[i].status === "fulfilled");
      const failedResults = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected",
      );
      return { succeededIds, failedResults };
    },
    onSuccess: ({ succeededIds, failedResults }) => {
      qc.invalidateQueries({ queryKey: ["team-members", teamId] });
      qc.invalidateQueries({ queryKey: ["duty-today", teamId] });
      setSelected((prev) => {
        const next = new Set(prev);
        succeededIds.forEach((id) => { next.delete(id); });
        return next;
      });
      if (failedResults.length > 0) {
        const reason = failedResults[0].reason;
        const message = reason instanceof Error ? reason.message : "";
        const isRotation = message.includes("schedule rotation");
        toast({
          variant: "destructive",
          title: "멤버 삭제 실패",
          description: isRotation
            ? "스케줄 로테이션에 포함된 멤버입니다. 해당 멤버가 포함된 스케줄을 먼저 정리해주세요."
            : `${failedResults.length}명의 멤버를 삭제하지 못했습니다.`,
        });
      }
    },
  });

  // ── handlers ──────────────────────────────────────────────────────────────
  function toggleSelect(memberId: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  }

  const selectableMembers = teamMembers.filter((tm) => !isPrimary(tm.member_id));
  const allSelectableChecked =
    selectableMembers.length > 0 &&
    selectableMembers.every((tm) => selected.has(tm.member_id));

  function toggleAll() {
    if (allSelectableChecked) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableMembers.map((tm) => tm.member_id)));
    }
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">팀원</h3>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setAddOpen(true)}>추가</Button>
          <Button
            size="sm"
            variant="outline"
            disabled={selected.size === 0 || deleteMut.isPending}
            onClick={() => deleteMut.mutate()}
          >
            {deleteMut.isPending ? "삭제 중…" : "삭제"}
          </Button>
        </div>
      </div>

      {/* table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 border-b">
              <th className="px-4 py-3 w-10">
                <Checkbox
                  checked={allSelectableChecked}
                  onCheckedChange={toggleAll}
                  disabled={selectableMembers.length === 0}
                />
              </th>
              <th className="text-left px-4 py-3 font-semibold">이름</th>
              <th className="text-left px-4 py-3 font-semibold">이메일</th>
            </tr>
          </thead>
          <tbody>
            {teamMembers.length === 0 ? (
              <tr>
                <td
                  colSpan={3}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  팀 멤버가 없습니다.
                </td>
              </tr>
            ) : (
              teamMembers.map((tm) => {
                const member = memberMap.get(tm.member_id);
                const primary = isPrimary(tm.member_id);
                const isChecked = selected.has(tm.member_id);

                return (
                  <tr key={tm.member_id} className="border-b last:border-0">
                    {/* checkbox */}
                    <td className="px-4 py-3">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleSelect(tm.member_id)}
                        disabled={primary}
                      />
                    </td>

                    {/* name */}
                    <td className="px-4 py-3">
                      {member?.name ?? getName(tm.member_id)}
                    </td>

                    {/* email */}
                    <td className="px-4 py-3 text-muted-foreground">
                      {member?.email ?? "-"}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* add modal */}
      <AddTeamMemberModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        allMembers={allMembers}
        teamMemberIds={teamMemberIds}
        onAdd={(memberId) => addMut.mutate(memberId)}
        onPhoneMissing={(m) => setPhoneMissingMember(m)}
        isPending={addMut.isPending}
      />

      {/* phone 미등록 안내 Dialog */}
      <PhoneMissingDialog
        member={phoneMissingMember}
        onClose={() => setPhoneMissingMember(null)}
      />
    </div>
  );
}

// ── PhoneMissingDialog ────────────────────────────────────────────────────────

function PhoneMissingDialog({
  member,
  onClose,
}: {
  member: MemberResponse | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!member} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>전화번호 미등록 멤버</DialogTitle>
          <DialogDescription>
            {member ? `${member.name} 멤버는 ` : ""}
            전화번호가 등록되어 있지 않아 팀에 추가할 수 없습니다.
            <br />
            멤버 정보 페이지에서 전화번호를 먼저 등록해 주세요.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" onClick={onClose}>
              확인
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
